import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { StatusBadge, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { EditAssetModal } from '../components/EditAssetModal';
import { assetsAPI, sitesAPI } from '../utils/api';
import { formatDecimal, formatDateTime } from '../utils/helpers';
import {
  Battery, Zap, Activity, Gauge, Clock, Cpu, BarChart3, Settings2, AlertCircle, RefreshCw, Pencil
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BASE_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'telemetry', label: 'Telemetry', icon: Activity },
  { id: 'metering', label: 'Metering', icon: Gauge },
];

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const [asset, setAsset] = useState(null);
  const [site, setSite] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [metering, setMetering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditAsset, setShowEditAsset] = useState(false);
  const { isAdmin } = useAuth();

  const TABS = BASE_TABS;

  const fetchAssetData = useCallback(async () => {
    try {
      const assetRes = await assetsAPI.getById(assetId);
      setAsset(assetRes.data);
      const [siteRes, telRes, meterRes] = await Promise.all([
        sitesAPI.getById(assetRes.data.site_id),
        assetsAPI.getTelemetry(assetId, 200),
        assetsAPI.getMetering(assetId).catch(() => ({ data: null })),
      ]);
      setSite(siteRes.data);
      setTelemetry(telRes.data || []);
      setMetering(meterRes.data);
    } catch (err) {
      console.error('Failed to load asset:', err);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => { fetchAssetData(); }, [fetchAssetData]);

  const setTab = (tab) => setSearchParams({ tab });

  if (loading) return (
    <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>
  );

  if (!asset) return (
    <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 p-8">
      <p className="text-slate-500">Asset not found</p>
    </div></div>
  );

  const breadcrumbs = [
    { label: 'Fleet', to: '/dashboard' },
    { label: site?.name || 'Site', to: `/sites/${asset.site_id}` },
    { label: asset.asset_name },
  ];

  const chartData = [...telemetry].reverse().map(t => ({
    time: new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    soc: t.soc_percent,
    discharge: t.discharge_power_kw || t.discharge_power_kwh || 0,
    charge: t.charge_power_kw || t.charge_power_kwh || 0,
    cumulative: t.cumulative_discharge_kwh,
  }));

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Breadcrumbs items={breadcrumbs} onBack={() => navigate(`/sites/${asset.site_id}`)} />

          {/* Asset Header */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm mb-6" data-testid="asset-header">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-center">
                  <Battery className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-slate-900">{asset.asset_name}</h1>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {asset.manufacturer && `${asset.manufacturer} `}
                    {asset.model && `${asset.model} `}
                    {asset.serial_number && `· SN: ${asset.serial_number}`}
                  </p>
                </div>
              </div>
              <StatusBadge status={asset.status} />
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={() => setShowEditAsset(true)} data-testid="edit-asset-btn" className="ml-3">
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={Zap} label="Power" value={`${formatDecimal(asset.installed_power_kw, 0)} kW`} />
              <Stat icon={Battery} label="Capacity" value={`${formatDecimal(asset.installed_capacity_kwh, 0)} kWh`} />
              <Stat icon={Gauge} label="SoC" value={metering?.current_soc_percent != null ? `${formatDecimal(metering.current_soc_percent, 1)}%` : 'N/A'} />
              <Stat icon={Clock} label="Last Data" value={asset.last_telemetry_at ? formatDateTime(asset.last_telemetry_at).split(',')[1]?.trim() || 'Recent' : 'Never'} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-white border border-slate-200 rounded-sm p-1 mb-6" data-testid="asset-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && <OverviewTab metering={metering} chartData={chartData} telemetry={telemetry} />}
          {activeTab === 'telemetry' && <TelemetryTab telemetry={telemetry} chartData={chartData} />}
          {activeTab === 'metering' && <MeteringTab metering={metering} />}
        </div>
      </div>
      <EditAssetModal
        open={showEditAsset}
        onClose={() => setShowEditAsset(false)}
        asset={asset}
        onUpdated={(updated) => setAsset(updated)}
      />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center space-x-3">
    <div className="w-9 h-9 bg-slate-50 rounded-sm flex items-center justify-center">
      <Icon className="w-4 h-4 text-slate-500" />
    </div>
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value}</p>
    </div>
  </div>
);

const OverviewTab = ({ metering, chartData, telemetry }) => {
  const isJem = metering?.jem_latest != null;
  const isPcsActual = metering?.data_quality_source === 'actual_pcs';
  const jemStatus = metering?.jem_latest?.charging_status;
  const statusLabels = { 1: 'Charging', 2: 'Discharging', 3: 'Idle/Standby' };
  const qualityLabel = isPcsActual ? 'PCS actual' : 'SoC-delta estimate';

  // Latest row carries the per-PCS breakdown (sub-components of the BESS).
  const latestWithBreakdown = (telemetry || []).find(t => Array.isArray(t?.pcs_breakdown) && t.pcs_breakdown.length > 0);
  const pcsUnits = latestWithBreakdown?.pcs_breakdown || [];
  const pcsAsOf = latestWithBreakdown?.created_at;

  return (
    <div className="space-y-6">
      {/* JEM Data Quality Notice */}
      {isJem && !isPcsActual && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start space-x-3" data-testid="jem-data-notice">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <p className="font-medium">JEM Portal — Energy data estimated via SoC-delta method</p>
            <p className="mt-0.5">
              Cumulative energy not available from JEM for this account. Charge/discharge values are estimated from SoC changes ({metering?.installed_capacity_kwh || 0} kWh capacity).
              {jemStatus && <span className="ml-2 font-medium">Current status: {statusLabels[jemStatus] || 'Unknown'}</span>}
            </p>
          </div>
        </div>
      )}
      {isJem && isPcsActual && (
        <div className="bg-green-50 border border-green-200 rounded-sm p-3 flex items-start space-x-3" data-testid="jem-pcs-notice">
          <Activity className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-green-800">
            <p className="font-medium">JEM Portal — Actual PCS metered data</p>
            <p className="mt-0.5">Cumulative energy sourced from PCS device (totalAcChargeKwh / totalAcDischargeKwh). Data quality: actual.</p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard title="Lifetime Discharge" value={`${formatDecimal(metering?.lifetime_units_kwh || 0, 1)} kWh`} subtitle={qualityLabel} color="blue" />
        <KpiCard title="This Month" value={`${formatDecimal(metering?.this_month?.billable_units_kwh || 0, 1)} kWh`} subtitle={metering?.this_month?.quality_flag === 'good' ? 'Cumulative method' : qualityLabel} color="green" />
        <KpiCard title="Today" value={`${formatDecimal(metering?.today_discharge_kwh || 0, 1)} kWh`} subtitle={qualityLabel} color="amber" />
      </div>

      {/* PCS Sub-component Breakdown */}
      {pcsUnits.length > 0 && (
        <PCSBreakdownPanel units={pcsUnits} asOf={pcsAsOf} />
      )}

      {/* SoC Chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">State of Charge</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData.slice(-100)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="time" stroke="#64748b" fontSize={11} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} domain={[0, 100]} />
            <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '2px' }} />
            <Area type="monotone" dataKey="soc" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} name="SoC (%)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
  );
};

const TelemetryTab = ({ telemetry, chartData }) => (
  <div className="space-y-6">
    {chartData.length > 0 && (
      <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-4">Power Flow</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData.slice(-100)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="time" stroke="#64748b" fontSize={11} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} />
            <Tooltip contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '2px' }} />
            <Legend />
            <Area type="monotone" dataKey="charge" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Charge" />
            <Area type="monotone" dataKey="discharge" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Discharge" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
    {/* Recent Telemetry Table */}
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
      <div className="p-4 border-b border-slate-200">
        <h3 className="font-semibold text-slate-900">Recent Readings ({telemetry.length})</h3>
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="data-table text-sm">
          <thead><tr>
            <th>Timestamp</th><th>SoC %</th><th>Charge kW</th><th>Discharge kW</th><th>Cumulative kWh</th><th>Quality</th>
          </tr></thead>
          <tbody>
            {telemetry.slice(0, 50).map((t, i) => (
              <tr key={t.id || t.timestamp || i}>
                <td className="font-mono text-xs">{formatDateTime(t.timestamp)}</td>
                <td>{t.soc_percent != null ? formatDecimal(t.soc_percent, 1) : '-'}</td>
                <td>{t.charge_power_kw != null ? formatDecimal(t.charge_power_kw, 2) : '-'}</td>
                <td>{t.discharge_power_kw != null ? formatDecimal(t.discharge_power_kw, 2) : '-'}</td>
                <td className="font-mono">{t.cumulative_discharge_kwh != null ? formatDecimal(t.cumulative_discharge_kwh, 2) : '-'}</td>
                <td><span className={`text-xs px-1.5 py-0.5 rounded ${t.quality_flag === 'good' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{t.quality_flag || 'good'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const MeteringTab = ({ metering }) => {
  if (!metering) return <div className="text-center py-12 text-slate-500">No metering data available</div>;

  const isJem = metering?.jem_latest != null;
  const isPcsActual = metering?.data_quality_source === 'actual_pcs';
  const qualityLabel = isPcsActual ? 'PCS actual metered' : 'SoC-delta estimate';

  return (
    <div className="space-y-6">
      {/* JEM Notice */}
      {isJem && !isPcsActual && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start space-x-3" data-testid="jem-metering-notice">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <p className="font-medium">JEM Portal — Cumulative energy not available from vendor</p>
            <p className="mt-0.5">Energy values are estimated using the SoC-delta method (SoC change x battery capacity).</p>
          </div>
        </div>
      )}
      {isJem && isPcsActual && (
        <div className="bg-green-50 border border-green-200 rounded-sm p-3 flex items-start space-x-3" data-testid="jem-metering-actual">
          <Activity className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-green-800">
            <p className="font-medium">JEM Portal — Actual PCS metered data</p>
            <p className="mt-0.5">Cumulative energy sourced from PCS device hardware meters. Data quality: actual.</p>
          </div>
        </div>
      )}

      {/* Metering KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Lifetime Units" value={`${formatDecimal(metering.lifetime_units_kwh, 2)} kWh`} subtitle={qualityLabel} color="blue" />
        <KpiCard title="This Month" value={`${formatDecimal(metering.this_month?.billable_units_kwh || 0, 2)} kWh`} subtitle={`Method: ${isPcsActual ? 'PCS cumulative' : metering.this_month?.calculation_method || 'N/A'}`} color="green" />
        <KpiCard title="Last Month" value={`${formatDecimal(metering.last_month?.billable_units_kwh || 0, 2)} kWh`} subtitle={`Quality: ${metering.last_month?.quality_flag || 'N/A'}`} color="slate" />
        <KpiCard title="Meter Resets" value={metering.reset_count || 0} subtitle={`Source: ${metering.data_quality_source || metering.data_quality}`} color={metering.reset_count > 0 ? 'amber' : 'green'} />
      </div>
      {/* Readings */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-4">Current Readings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ReadingCard label="Latest Raw Cumulative" value={metering.latest_reading?.raw_cumulative_kwh} unit="kWh" timestamp={metering.latest_reading?.timestamp} />
          <ReadingCard label="Latest Normalized" value={metering.latest_reading?.normalized_cumulative_kwh} unit="kWh" />
          {metering.this_month?.opening_reading && (
            <ReadingCard label="Opening Reading (This Month)" value={metering.this_month.opening_reading.normalized_value} unit="kWh" timestamp={metering.this_month.opening_reading.timestamp} />
          )}
          {metering.this_month?.closing_reading && (
            <ReadingCard label="Closing Reading (This Month)" value={metering.this_month.closing_reading.normalized_value} unit="kWh" timestamp={metering.this_month.closing_reading.timestamp} />
          )}
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ title, value, subtitle, color = 'blue' }) => {
  const colors = {
    blue: 'border-l-blue-500 bg-blue-50/30',
    green: 'border-l-green-500 bg-green-50/30',
    amber: 'border-l-amber-500 bg-amber-50/30',
    slate: 'border-l-slate-400 bg-slate-50/30',
  };
  return (
    <div className={`border border-slate-200 border-l-4 ${colors[color]} rounded-sm p-4 shadow-sm`} data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-xs text-slate-500 mb-1">{title}</p>
      <p className="text-lg font-bold text-slate-900">{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
};

const ReadingCard = ({ label, value, unit, timestamp }) => (
  <div className="border border-slate-100 rounded-sm p-4 bg-slate-50/50">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className="text-lg font-bold text-slate-900">
      {value != null ? `${formatDecimal(value, 2)} ${unit}` : 'N/A'}
    </p>
    {timestamp && <p className="text-xs text-slate-400 mt-1">{formatDateTime(timestamp)}</p>}
  </div>
);



const PCSBreakdownPanel = ({ units, asOf }) => {
  const totalDischarge = units.reduce((s, u) => s + (typeof u.cumulative_discharge_kwh === 'number' ? u.cumulative_discharge_kwh : 0), 0);
  const totalCharge = units.reduce((s, u) => s + (typeof u.cumulative_charge_kwh === 'number' ? u.cumulative_charge_kwh : 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="pcs-breakdown-panel">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center space-x-2">
            <Cpu className="w-4 h-4 text-slate-500" />
            <span>PCS Sub-components ({units.length})</span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Per-unit breakdown of the BESS — sums to the aggregate values above</p>
        </div>
        {asOf && (
          <span className="text-[10px] text-slate-400 font-mono">As of {formatDateTime(asOf)}</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-100">
        {units.map((u, i) => {
          const ap = typeof u.ac_power_kw === 'number' ? u.ac_power_kw : null;
          const flowLabel = ap == null ? '—' : ap < 0 ? 'Charging' : ap > 0 ? 'Discharging' : 'Idle';
          const flowColor = ap == null ? 'text-slate-400' : ap < 0 ? 'text-emerald-600' : ap > 0 ? 'text-amber-600' : 'text-slate-500';
          return (
            <div key={u.pcs_id || i} className="bg-white p-4 space-y-2" data-testid={`pcs-unit-${u.pcs_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-semibold text-slate-900">PCS #{u.pcs_id}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${u.online === false ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {u.online === false ? 'Offline' : 'Online'}
                  </span>
                </div>
                <span className={`text-xs font-medium ${flowColor}`}>{flowLabel}</span>
              </div>
              {u.online === false ? (
                <p className="text-xs text-slate-500 italic">{u.error || 'No data this poll'}</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
                  <PCSStat label="SoC" value={u.soc_percent != null ? `${formatDecimal(u.soc_percent, 1)} %` : '—'} />
                  <PCSStat label="AC Power" value={ap != null ? `${formatDecimal(Math.abs(ap), 2)} kW` : '—'} />
                  <PCSStat label="Cum. Discharge" value={u.cumulative_discharge_kwh != null ? `${formatDecimal(u.cumulative_discharge_kwh, 2)} kWh` : '—'} mono />
                  <PCSStat label="Cum. Charge" value={u.cumulative_charge_kwh != null ? `${formatDecimal(u.cumulative_charge_kwh, 2)} kWh` : '—'} mono />
                  <PCSStat label="Today Discharge" value={u.today_discharge_kwh != null ? `${formatDecimal(u.today_discharge_kwh, 2)} kWh` : '—'} mono />
                  <PCSStat label="Grid" value={u.grid_connected == null ? '—' : u.grid_connected ? 'Connected' : 'Disconnected'} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs">
        <span className="text-slate-500">BESS-aggregate (sum of units):</span>
        <span className="font-mono text-slate-900">
          Discharge <strong>{formatDecimal(totalDischarge, 2)} kWh</strong> · Charge <strong>{formatDecimal(totalCharge, 2)} kWh</strong>
        </span>
      </div>
    </div>
  );
};

const PCSStat = ({ label, value, mono }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`text-sm text-slate-900 ${mono ? 'font-mono' : 'font-medium'}`}>{value}</p>
  </div>
);
